import { ReactElement } from "react";
import { Timestamp, FieldValue } from "firebase/firestore";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Snackbar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CleanupIcon from "@mui/icons-material/CleaningServices";
import { WorkspaceMemberWithDetails, WorkspaceInvitation, ValuesVisibilityMode, WorkspaceRole, TimeFormat, DateFormat, ThemeMode } from "../types";
import LoadingSpinner from "../components/LoadingSpinner";
import { isDemoMode } from "../utils/demoMode";
import { isBypassAuthEnabled } from "../utils/bypassAuth";
import EditableTextField from "../components/EditableTextField";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import StandardDialog from "../components/StandardDialog";
import TransactionFieldsSettings from "./TransactionFieldsSettings/TransactionFieldsSettings";

// Hooks
import { usePermissionHelpers } from "./Activity/hooks/usePermissionHelpers";
import { useWorkspace } from "./WorkspaceSettings/hooks/useWorkspace";
import { useWorkspaceMembers } from "./WorkspaceSettings/hooks/useWorkspaceMembers";
import { useWorkspaceInvitations } from "./WorkspaceSettings/hooks/useWorkspaceInvitations";
import { useWorkspaceSettings } from "./WorkspaceSettings/hooks/useWorkspaceSettings";
import { useWorkspaceUIState } from "./WorkspaceSettings/hooks/useWorkspaceUIState";
import { useWorkspaceHelpers } from "./WorkspaceSettings/hooks/useWorkspaceHelpers";
import { useWorkspaceHandlers } from "./WorkspaceSettings/hooks/useWorkspaceHandlers";
import { useWorkspaceMemberHandlers } from "./WorkspaceSettings/hooks/useWorkspaceMemberHandlers";
import { useWorkspaceActionHandlers } from "./WorkspaceSettings/hooks/useWorkspaceActionHandlers";

// Components
import InviteUserDialog from "./WorkspaceSettings/components/InviteUserDialog";
import InvitationSuccessDialog from "./WorkspaceSettings/components/InvitationSuccessDialog";
import RemoveMemberDialog from "./WorkspaceSettings/components/RemoveMemberDialog";
import LeaveWorkspaceDialog from "./WorkspaceSettings/components/LeaveWorkspaceDialog";
import DeleteWorkspaceDialog from "./WorkspaceSettings/components/DeleteWorkspaceDialog";

export type WorkspaceSection = "information" | "transactions" | "members" | "preferences" | "danger";

export interface WorkspaceSettingsProps {
  workspaceId: string;
  /** Which section to show. When omitted (e.g. legacy link), defaults to "information". */
  section?: WorkspaceSection;
  /** When true, use full page width (no horizontal padding). Used when embedded in Members & Permissions. */
  fullWidth?: boolean;
}

function WorkspaceSettings({ workspaceId, section: sectionProp, fullWidth = false }: WorkspaceSettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const section = sectionProp ?? "information";

  // Handler to ensure wheel events scroll the panel even when pointer is over content that
  // captures wheel (e.g. Transaction fields DnD). Use capture phase so we run before children.
  const handlePanelWheel = (e: React.WheelEvent<HTMLElement>) => {
    const scrollable = e.currentTarget;
    const style = window.getComputedStyle(scrollable);
    if (style.overflowY === "auto" || style.overflowY === "scroll") {
      scrollable.scrollTop += e.deltaY * 0.3;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Data fetching hooks
  const {
    workspace,
    loading: workspaceLoading,
    isOwner,
    currentUserId,
    setWorkspace,
    setIsOwner,
  } = useWorkspace({ workspaceId });

  const {
    members,
    currentUserRole,
    setMembers,
    setCurrentUserRole,
  } = useWorkspaceMembers({ workspaceId, workspace });

  const {
    invitations,
    setInvitations,
  } = useWorkspaceInvitations({ workspaceId });

  const {
    valuesVisibilityMode,
    permissions,
    customRoles,
    customRoleNames,
    timeFormat,
    dateFormat,
    themeMode,
    transactionIdPrefix,
    allowExpenseFee,
    allowTransferFee,
    setValuesVisibilityMode,
    setPermissions,
    setTimeFormat,
    setDateFormat,
    setThemeMode,
    setTransactionIdPrefix,
    setAllowExpenseFee,
    setAllowTransferFee,
  } = useWorkspaceSettings({ workspaceId });

  const { canEditPreferences } = usePermissionHelpers({
    permissions,
    currentUserRole: currentUserRole ?? "user",
    workspaceOwnerId: workspace?.ownerId ?? null,
  });

  // UI state hook
  const uiState = useWorkspaceUIState();

  // Helpers hook
  const { formatDate, getRoleDisplayName, canManageWorkspaceConfig, canManageMembers, validateEmail } = useWorkspaceHelpers({
    isOwner,
    currentUserRole,
    permissions,
    customRoles,
    customRoleNames,
  });

  // Workspace handlers
  const workspaceHandlers = useWorkspaceHandlers({
    workspaceId,
    workspace,
    canManageWorkspaceConfig,
    setWorkspace,
    setSaving: uiState.setSaving,
    setUploadingLogo: uiState.setUploadingLogo,
    setRemoveLogoDialogOpen: uiState.setRemoveLogoDialogOpen,
    setSnackbarOpen: uiState.setSnackbarOpen,
    setSnackbarElement: uiState.setSnackbarElement,
    fileInputRef: uiState.fileInputRef,
  });

  // Handle time format change
  const handleTimeFormatChange = async (format: "12h" | "24h") => {
    await workspaceHandlers.handleTimeFormatChange(format);
    setTimeFormat(format);
  };

  // Handle date format change
  const handleDateFormatChange = async (format: DateFormat) => {
    await workspaceHandlers.handleDateFormatChange(format);
    setDateFormat(format);
  };

  // Handle theme mode change
  const handleThemeModeChange = async (mode: ThemeMode) => {
    await workspaceHandlers.handleThemeModeChange(mode);
    setThemeMode(mode);
  };

  // Member handlers
  const memberHandlers = useWorkspaceMemberHandlers({
    workspaceId,
    isOwner,
    currentUserRole,
    workspaceOwnerId: workspace?.ownerId ?? null,
    canManageMembers,
    members,
    invitations,
    setInviteDialogOpen: uiState.setInviteDialogOpen,
    setInviteEmail: uiState.setInviteEmail,
    setInviteRole: uiState.setInviteRole,
    setInviting: uiState.setInviting,
    setInviteError: uiState.setInviteError,
    setCreatedInvitationLink: uiState.setCreatedInvitationLink,
    setInvitationSuccessDialogOpen: uiState.setInvitationSuccessDialogOpen,
    setEmailSent: uiState.setEmailSent,
    setEmailError: uiState.setEmailError,
    setRemoveMemberDialogOpen: uiState.setRemoveMemberDialogOpen,
    setMemberToRemove: uiState.setMemberToRemove,
    setRemoving: uiState.setRemoving,
    setChangingRole: uiState.setChangingRole,
    setResendingInvite: uiState.setResendingInvite,
    setSnackbarOpen: uiState.setSnackbarOpen,
    setSnackbarElement: uiState.setSnackbarElement,
    validateEmail,
    inviteEmail: uiState.inviteEmail,
    inviteRole: uiState.inviteRole,
    memberToRemove: uiState.memberToRemove,
  });

  // Action handlers
  const actionHandlers = useWorkspaceActionHandlers({
    workspaceId,
    workspace,
    isOwner,
    setDeleteDialogOpen: uiState.setDeleteDialogOpen,
    setDeleting: uiState.setDeleting,
    setDeleteWorkspaceName: uiState.setDeleteWorkspaceName,
    allowNavigationRef: uiState.allowNavigationRef,
    setLeaveDialogOpen: uiState.setLeaveDialogOpen,
    setLeaving: uiState.setLeaving,
    setLeaveWorkspaceName: uiState.setLeaveWorkspaceName,
    setSnackbarOpen: uiState.setSnackbarOpen,
    setSnackbarElement: uiState.setSnackbarElement,
    deleting: uiState.deleting,
    leaveWorkspaceName: uiState.leaveWorkspaceName,
    deleteWorkspaceName: uiState.deleteWorkspaceName,
  });

  if (workspaceLoading) {
    return <LoadingSpinner fullPage={true} />;
  }

  // Show loading while redirecting (workspace not found case)
  if (!workspace) {
    return <LoadingSpinner fullPage={true} />;
  }

  // Combine members and invitations for display
  type InvitationMemberDisplay = {
    id: string;
    email: string;
    status: "invited" | "declined";
    role: "admin" | "user" | "viewer" | "member";
    joinedAt: Timestamp | FieldValue;
  };
  
  // Filter members: in demo mode, exclude owner from the list
  const displayMembers = isDemoMode() && workspace
    ? members.filter((m) => m.id !== workspace.ownerId)
    : members;
  
  const allMembers: (WorkspaceMemberWithDetails | InvitationMemberDisplay)[] = [
    ...displayMembers,
    ...invitations.map((inv) => ({
      id: inv.id,
      email: inv.email,
      status: (inv.status === "pending" ? "invited" : inv.status === "declined" ? "declined" : "invited") as "invited" | "declined",
      role: (inv.role || "member") as "admin" | "user" | "viewer" | "member",
      joinedAt: inv.createdAt,
    })),
  ];

  const cardMargin = isMobile ? 1 : 3; // 1 = 8px, 3 = 24px (MUI theme spacing)

  // Single panel: show only the section from main Settings list (no second sidebar)
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        bgcolor: "background.default",
        overflow: "auto",
        ...(fullWidth ? { py: 0, px: 0, pb: 0 } : { py: 0, px: 0, pb: 0 }),
      }}
      onWheelCapture={handlePanelWheel}
    >
          {section === "information" && (
            <>
            <Card sx={{ mb: cardMargin }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>Workspace</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Workspace Logo</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar src={workspace.logoUrl} sx={{ width: 80, height: 80, bgcolor: "primary.main", fontSize: "2rem" }}>
                        {workspace.name[0]?.toUpperCase()}
                      </Avatar>
                      {canManageWorkspaceConfig() && (
                        <Stack direction="row" spacing={1}>
                          <input ref={uiState.fileInputRef} type="file" accept="image/*" onChange={workspaceHandlers.handleLogoUpload} style={{ display: "none" }} />
                          <Button variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => uiState.fileInputRef.current?.click()} disabled={uiState.uploadingLogo} size="small">
                            {uiState.uploadingLogo ? "Uploading..." : "Upload Logo"}
                          </Button>
                          {workspace.logoUrl && (
                            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={workspaceHandlers.handleRemoveLogoClick} disabled={uiState.uploadingLogo} size="small">
                              Remove
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <EditableTextField value={workspace.name} onSave={workspaceHandlers.handleSaveName} editable={canManageWorkspaceConfig()} saving={uiState.saving} label="Workspace Name" variant="body1" placeholder="Enter workspace name" />
                  </Box>
                  <Box>
                    <EditableTextField
                      value={transactionIdPrefix}
                      onSave={async (value) => { await workspaceHandlers.handleSaveTransactionIdPrefix(value); setTransactionIdPrefix(value.trim()); }}
                      editable={canManageWorkspaceConfig()}
                      saving={uiState.saving}
                      label="Transaction ID prefix"
                      variant="body1"
                      placeholder="e.g. DV"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Transactions will show as prefix-1, prefix-2, etc. (e.g. DV-1, DV-2). This field is required and cannot be empty.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Created Date</Typography>
                    <Typography variant="body1">{formatDate(workspace.createdAt as Timestamp)}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            {!isOwner && (
              <Card sx={{ border: "2px solid", borderColor: "warning.main", mb: cardMargin }}>
                <CardContent>
                  <Typography variant="h5" color="warning.main" gutterBottom>Leave Workspace</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" component="span">
                      You can leave this workspace at any time. Your transactions, accounts, and all data you added will be preserved. If you're added back to the workspace later, everything will continue working as before.{" "}
                      <Typography component="span" variant="body2" onClick={() => uiState.setLeaveDialogOpen(true)} sx={{ color: "warning.main", textDecoration: "underline", cursor: "pointer", fontWeight: "medium", "&:hover": { color: "warning.dark" } }}>Leave Workspace</Typography>
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            )}
            {(isOwner || isDemoMode()) && (
              <Card sx={{ mb: cardMargin }}>
                <CardContent sx={{ pt: "16px", pr: "16px", pb: "16px", pl: "16px" }}>
                  <Typography variant="h5" color="error" gutterBottom>Danger Zone</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.primary">
                    Deleting this workspace will permanently delete all accounts, transactions, categories, and all associated data. This action cannot be undone.{" "}
                    <Typography component="span" variant="body2" onClick={() => uiState.setDeleteDialogOpen(true)} sx={{ color: "error.main", textDecoration: "underline", cursor: "pointer", "&:hover": { color: "error.dark" } }}>Delete Workspace</Typography>
                  </Typography>
                </CardContent>
              </Card>
            )}
            </>
          )}
          {section === "transactions" && (
            <>
              <Card sx={{ mb: cardMargin }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>Transaction configuration</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {canEditPreferences() ? (
                    <Stack spacing={2}>
                      <Box>
                        <FormControlLabel
                          control={<Switch checked={allowExpenseFee} onChange={async (_, checked) => { await workspaceHandlers.handleAllowExpenseFeeChange(checked); setAllowExpenseFee(checked); }} disabled={!canEditPreferences()} />}
                          label="Enable expense fee"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          When enabled, users can add an optional fee (amount + account) when creating or editing an expense. Useful for company workspaces (e.g. bank fees on payments). When you add workspace type (Company / Personal), Company can default this on.
                        </Typography>
                      </Box>
                      <Box>
                        <FormControlLabel
                          control={<Switch checked={allowTransferFee} onChange={async (_, checked) => { await workspaceHandlers.handleAllowTransferFeeChange(checked); setAllowTransferFee(checked); }} disabled={!canEditPreferences()} />}
                          label="Enable transfer fee"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          When enabled, users can add an optional fee (amount + account) when creating or editing a transfer. Only affects new transactions; existing transactions and their fees are unchanged. When off, the add-fee option is hidden for new transfers.
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Alert severity="info">You don't have permission to edit preferences. Contact the workspace owner or an admin to change these settings.</Alert>
                  )}
                </CardContent>
              </Card>
              <Card sx={{ mb: cardMargin }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>Transaction fields</Typography>
                  <Divider sx={{ mb: 2 }} />
                  {!canEditPreferences() && (
                    <Alert severity="info" sx={{ mb: 2 }}>You don't have permission to edit preferences. Transaction fields are read-only.</Alert>
                  )}
                  <TransactionFieldsSettings workspaceId={workspaceId} readOnly={!canEditPreferences()} />
                </CardContent>
              </Card>
            </>
          )}
          {section === "members" && (
            <Card sx={{ mb: cardMargin }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  <Typography variant="h5">Members</Typography>
                  {((canManageWorkspaceConfig() || canManageMembers()) || isDemoMode()) && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {(canManageWorkspaceConfig() || isDemoMode()) && (
                        <Button variant="outlined" startIcon={<CleanupIcon />} onClick={() => uiState.setCleanupOrphanedDialogOpen(true)} disabled={uiState.cleaningUp} size="small">
                          {uiState.cleaningUp ? "Cleaning..." : "Clean Up Orphaned"}
                        </Button>
                      )}
                      {(canManageMembers() || isDemoMode()) && (
                        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => uiState.setInviteDialogOpen(true)}>Invite User</Button>
                      )}
                    </Stack>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {allMembers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No members yet</Typography>
                ) : (
                  <Stack spacing={2}>
                    {allMembers.map((member) => {
                      const isInvited = "status" in member && (member.status === "invited" || member.status === "declined");
                      const invitation = isInvited ? invitations.find((inv) => inv.id === member.id) : null;
                      const isDeclined = invitation?.status === "declined";
                      const isWorkspaceOwner = workspace && member.id === workspace.ownerId;
                      const canRemoveThisMember = (isOwner || canManageMembers()) && !isWorkspaceOwner && member.id !== currentUserId;
                      const openRemoveDialog = (): void => {
                        if (!("id" in member)) return;
                        const getMemberRole = (): "owner" | "member" | "admin" | "user" | "viewer" => {
                          if ("role" in member) { if (member.role === "member" || !member.role) return "user"; return member.role; }
                          return "user";
                        };
                        const memberData: WorkspaceMemberWithDetails = isInvited
                          ? { id: member.id, role: "user", joinedAt: Timestamp.now(), email: "email" in member ? member.email : undefined, status: "invited" }
                          : { id: member.id, role: getMemberRole(), joinedAt: "joinedAt" in member ? member.joinedAt : Timestamp.now(), name: "name" in member ? member.name : undefined, email: "email" in member ? member.email : undefined, photoURL: "photoURL" in member ? member.photoURL : undefined, status: "active" };
                        uiState.setMemberToRemove(memberData);
                        uiState.setRemoveMemberDialogOpen(true);
                      };
                      return (
                        <Paper key={member.id} variant="outlined" sx={{ p: 2, position: "relative" }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Avatar src={"photoURL" in member ? member.photoURL : undefined} sx={{ width: 48, height: 48, bgcolor: "primary.main", flexShrink: 0 }}>
                                {("name" in member && member.name?.[0]?.toUpperCase()) || ("email" in member && member.email?.[0]?.toUpperCase()) || "?"}
                              </Avatar>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                                  <Typography variant="body1" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                                    {(() => {
                                      const isWorkspaceMember = "name" in member;
                                      if (isWorkspaceMember && member.name && member.name.trim()) return member.name;
                                      if (member.email && member.email.trim()) return isDemoMode() ? "demouser@budgettrace.com" : member.email;
                                      return `Unknown User (${member.id.substring(0, 8)}...)`;
                                    })()}
                                  </Typography>
                                  {isInvited && <Chip label={isDeclined ? "Declined" : "Invited"} color={isDeclined ? "error" : "warning"} size="small" />}
                                </Stack>
                                {(() => {
                                  const isWorkspaceMember = "name" in member;
                                  const displayEmail = member.email && member.email.trim();
                                  const displayName = isWorkspaceMember ? (member.name && member.name.trim()) : "";
                                  if (displayEmail && displayEmail !== displayName) {
                                    return <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: "100%" }}>{isDemoMode() ? "demouser@budgettrace.com" : displayEmail}</Typography>;
                                  }
                                  return null;
                                })()}
                                {isInvited && invitation && (
                                  <Typography variant="caption" color={isDeclined ? "error.main" : "text.secondary"}>
                                    {isDeclined ? "Declined" : "Invited"} {formatDate(invitation.createdAt as Timestamp)}{invitation.role && ` as ${getRoleDisplayName(invitation.role)}`}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                              {!isInvited && (
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                    value={"role" in member ? (member.role === "member" ? "user" : member.role) : "user"}
                                    disabled={(!canManageWorkspaceConfig() && !isDemoMode() && !isBypassAuthEnabled()) || uiState.changingRole === member.id || (workspace && member.id === workspace.ownerId && ("role" in member && member.role === "owner") && !isBypassAuthEnabled())}
                                    onChange={(e) => {
                                      const newRole: WorkspaceRole = e.target.value as WorkspaceRole;
                                      const currentRole = "role" in member ? member.role : "user";
                                      if (currentRole !== "owner" || newRole === "owner") memberHandlers.handleRoleChange(member.id, newRole);
                                    }}
                                    sx={{ "& .MuiSelect-select": { py: 0.75 } }}
                                  >
                                    <MenuItem value="owner">Owner</MenuItem>
                                    <MenuItem value="admin">{getRoleDisplayName("admin")}</MenuItem>
                                    <MenuItem value="user">{getRoleDisplayName("user")}</MenuItem>
                                    <MenuItem value="viewer">{getRoleDisplayName("viewer")}</MenuItem>
                                    {customRoles.map((role) => <MenuItem key={role.id} value={role.id}>{getRoleDisplayName(role.id)}</MenuItem>)}
                                  </Select>
                                </FormControl>
                              )}
                              {(canManageMembers() || isDemoMode()) && (
                                <>
                                  {isInvited && invitation && !isDeclined && (
                                    <Button size="small" variant="outlined" onClick={() => memberHandlers.handleResendInvitation(invitation.id)} disabled={uiState.resendingInvite === invitation.id}>
                                      {uiState.resendingInvite === invitation.id ? "Resending..." : "Resend"}
                                    </Button>
                                  )}
                                  {!isMobile && canRemoveThisMember && (
                                    <IconButton color="error" size="small" onClick={openRemoveDialog}><DeleteIcon /></IconButton>
                                  )}
                                </>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}
          {section === "preferences" && (
            <Card sx={{ mb: cardMargin }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>Workspace Configuration</Typography>
                <Divider sx={{ mb: 2 }} />
                {canEditPreferences() ? (
                  <Stack spacing={2}>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel>Appearance</InputLabel>
                        <Select value={themeMode} label="Appearance" onChange={(e) => handleThemeModeChange(e.target.value as ThemeMode)} renderValue={(v) => ({ light: "Light", dark: "Dark" }[v])}>
                          <MenuItem value="light"><Box><Typography variant="body2" fontWeight="medium">Light</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Use light backgrounds and dark text across the app.</Typography></Box></MenuItem>
                          <MenuItem value="dark"><Box><Typography variant="body2" fontWeight="medium">Dark</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Use dark backgrounds and light text across the app.</Typography></Box></MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel>Visibility Mode</InputLabel>
                        <Select value={valuesVisibilityMode} label="Visibility Mode" onChange={(e) => workspaceHandlers.handleVisibilityModeChange(e.target.value as ValuesVisibilityMode)} renderValue={(v) => ({ "manual": "Manual", "always-closed": "Always Keep Closed", "always-visible": "Always Visible" }[v])}>
                          <MenuItem value="manual"><Box><Typography variant="body2" fontWeight="medium">Manual</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>User controls visibility. Values stay visible/hidden until user changes them.</Typography></Box></MenuItem>
                          <MenuItem value="always-closed"><Box><Typography variant="body2" fontWeight="medium">Always Keep Closed</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>When user clicks eye icon, values stay hidden even after page refresh or navigation.</Typography></Box></MenuItem>
                          <MenuItem value="always-visible"><Box><Typography variant="body2" fontWeight="medium">Always Visible</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>When user closes values, they become visible again after page refresh or navigation.</Typography></Box></MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel>Time Format</InputLabel>
                        <Select value={timeFormat} label="Time Format" onChange={(e) => handleTimeFormatChange(e.target.value as "12h" | "24h")} renderValue={(v) => ({ "12h": "12-hour (AM/PM)", "24h": "24-hour" }[v])}>
                          <MenuItem value="12h"><Box><Typography variant="body2" fontWeight="medium">12-hour (AM/PM)</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Display time in 12-hour format with AM/PM indicators (e.g., 2:30 PM).</Typography></Box></MenuItem>
                          <MenuItem value="24h"><Box><Typography variant="body2" fontWeight="medium">24-hour</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Display time in 24-hour format (e.g., 14:30).</Typography></Box></MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel>Date Format</InputLabel>
                        <Select value={dateFormat} label="Date Format" onChange={(e) => handleDateFormatChange(e.target.value as DateFormat)} renderValue={(v) => v}>
                          <MenuItem value="MM/DD/YYYY"><Box><Typography variant="body2" fontWeight="medium">MM/DD/YYYY</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Display dates with month first (e.g., Jan 15, 2024).</Typography></Box></MenuItem>
                          <MenuItem value="DD/MM/YYYY"><Box><Typography variant="body2" fontWeight="medium">DD/MM/YYYY</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Display dates with day first (e.g., 15 Jan 2024).</Typography></Box></MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="info">You don't have permission to edit preferences. Contact the workspace owner or an admin to change these settings.</Alert>
                )}
              </CardContent>
            </Card>
          )}
      {/* Dialogs */}
      <InviteUserDialog
          open={uiState.inviteDialogOpen}
          onClose={() => { uiState.setInviteDialogOpen(false); uiState.setInviteEmail(""); uiState.setInviteRole("user"); uiState.setInviteError(""); }}
          onInvite={memberHandlers.handleInviteUser}
          inviteEmail={uiState.inviteEmail}
          setInviteEmail={uiState.setInviteEmail}
          inviteRole={uiState.inviteRole}
          setInviteRole={uiState.setInviteRole}
          inviteError={uiState.inviteError}
          setInviteError={uiState.setInviteError}
          inviting={uiState.inviting}
          customRoles={customRoles}
          customRoleNames={customRoleNames}
          getRoleDisplayName={getRoleDisplayName}
        />
        <InvitationSuccessDialog open={uiState.invitationSuccessDialogOpen} onClose={() => { uiState.setInvitationSuccessDialogOpen(false); uiState.setEmailSent(false); uiState.setEmailError(undefined); uiState.setCreatedInvitationLink(null); }} emailSent={uiState.emailSent} emailError={uiState.emailError} createdInvitationLink={uiState.createdInvitationLink} />
        <RemoveMemberDialog open={uiState.removeMemberDialogOpen} onClose={() => { uiState.setRemoveMemberDialogOpen(false); uiState.setMemberToRemove(null); }} onConfirm={memberHandlers.handleRemoveMember} memberToRemove={uiState.memberToRemove} removing={uiState.removing} />
        <DeleteConfirmationDialog open={uiState.removeLogoDialogOpen} onClose={() => uiState.setRemoveLogoDialogOpen(false)} onConfirm={workspaceHandlers.handleRemoveLogoConfirm} itemName="workspace logo" itemType="logo" title="Remove Logo" message="Are you sure you want to remove the workspace logo? This action cannot be undone." disabled={uiState.uploadingLogo} confirmButtonText={uiState.uploadingLogo ? "Removing..." : "Remove Logo"} />
        <LeaveWorkspaceDialog open={uiState.leaveDialogOpen} onClose={() => { if (!uiState.leaving) { uiState.setLeaveDialogOpen(false); uiState.setLeaveWorkspaceName(""); } }} onConfirm={actionHandlers.handleLeaveWorkspace} workspace={workspace} leaveWorkspaceName={uiState.leaveWorkspaceName} setLeaveWorkspaceName={uiState.setLeaveWorkspaceName} leaving={uiState.leaving} />
        <DeleteWorkspaceDialog open={uiState.deleteDialogOpen} onClose={() => { if (!uiState.deleting) { uiState.setDeleteDialogOpen(false); uiState.setDeleteWorkspaceName(""); } }} onConfirm={actionHandlers.handleDeleteWorkspace} workspace={workspace} deleteWorkspaceName={uiState.deleteWorkspaceName} setDeleteWorkspaceName={uiState.setDeleteWorkspaceName} deleting={uiState.deleting} />
        <StandardDialog open={uiState.cleanupOrphanedDialogOpen} onClose={() => { if (!uiState.cleaningUp) uiState.setCleanupOrphanedDialogOpen(false); }} title="Clean Up Orphaned Members" confirmText={uiState.cleaningUp ? "Cleaning..." : "Continue"} onConfirm={async () => { uiState.setCleaningUp(true); try { const result = await memberHandlers.handleCleanupOrphanedMembers(); uiState.setCleanupOrphanedDialogOpen(false); if (result.removed > 0) uiState.setSnackbarElement(<Alert severity="success">Cleanup complete: Assigned {result.transactionsAssigned} transaction(s) to Developer, removed {result.removed} orphaned member(s){result.userDocsDeleted > 0 && `, deleted ${result.userDocsDeleted} user document(s)`}{result.orphaned.length > result.removed && ` (${result.orphaned.length - result.removed} failed)`}</Alert>); else if (result.orphaned.length > 0) uiState.setSnackbarElement(<Alert severity="warning">Found {result.orphaned.length} orphaned member(s) but couldn't remove them. Check console for details.</Alert>); else uiState.setSnackbarElement(<Alert severity="info">No orphaned members found</Alert>); uiState.setSnackbarOpen(true); } catch (e) { console.error(e); uiState.setSnackbarElement(<Alert severity="error">{e instanceof Error ? e.message : "Failed to clean up orphaned members"}</Alert>); uiState.setSnackbarOpen(true); } finally { uiState.setCleaningUp(false); } }} confirmDisabled={uiState.cleaningUp} confirmColor="primary" disableClose={uiState.cleaningUp} maxWidth="sm"><Alert severity="warning" sx={{ mb: 2 }}><ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}><li>Assign all transactions from orphaned members to Developer user</li><li>Remove the orphaned members (members whose user accounts no longer exist)</li></ul></Alert><Typography sx={{ textAlign: "center" }}>Are you sure you want to continue?</Typography></StandardDialog>
      <Snackbar open={uiState.snackbarOpen} autoHideDuration={6000} onClose={() => { uiState.setSnackbarOpen(false); uiState.setSnackbarElement(null); }} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => { uiState.setSnackbarOpen(false); uiState.setSnackbarElement(null); }} severity="info" sx={{ width: "100%" }}>{uiState.snackbarElement || uiState.snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
}

export default WorkspaceSettings;
